import _createClass from '../../../@babel/runtime/helpers/esm/createClass.js';
import _classCallCheck from '../../../@babel/runtime/helpers/esm/classCallCheck.js';
import _inherits from '../../../@babel/runtime/helpers/esm/inherits.js';
import _possibleConstructorReturn from '../../../@babel/runtime/helpers/esm/possibleConstructorReturn.js';
import _getPrototypeOf from '../../../@babel/runtime/helpers/esm/getPrototypeOf.js';
import { createUniqueTag } from './createUniqueTag.js';
import { getFromGlobalTagsCache, addToGlobalTagsCache } from './globalTagsCache.js';

function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }
function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

/**
 * Checks if klass is a subclass of native HTMLElement or polyfilled HTMLElement.
 * We manually loop over the prototpe, so we can detect if we extend from native HTMLElement
 * or the polyfilled one from scoped-custom-element-registry (window.HTMLElement)
 * @param {typeof HTMLElement} klass a class, like LitElement
 * @returns {boolean}
 */
function extendsHTMLElement(klass) {
  var currentClass = klass;
  while (currentClass) {
    // currentClass could either be:
    // 1. unpatched, native HTMLElement (when polyfill not loaded this is always the case), or:
    // 2. patched window.HTMLElement (can be the case when polyfill is loaded)
    // If polyfill is loaded and currentClass is the native HTMLElement, we don't have a
    // reference to it (polyfill calls it NativeHTMLElement, but doesn't expose it),
    // so we check its name.
    if (currentClass === window.HTMLElement || currentClass.name === 'HTMLElement') {
      return true;
    }
    currentClass = Object.getPrototypeOf(currentClass);
  }
  return false;
}

/**
 * Defines a custom element
 *
 * @param {string} tagName
 * @param {typeof HTMLElement} klass
 * @param {CustomElementRegistry} registry
 */
var defineElement = function defineElement(tagName, klass) {
  var registry = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : customElements;
  addToGlobalTagsCache(tagName, klass);
  registry.define(tagName, /*#__PURE__*/function (_klass) {
    _inherits(_class, _klass);
    var _super = _createSuper(_class);
    function _class() {
      _classCallCheck(this, _class);
      return _super.apply(this, arguments);
    }
    return _createClass(_class);
  }(klass));
};

/**
 * Stores a lazy element in the cache to be used in future
 *
 * @param {string} tagName
 * @param {CustomElementRegistry} registry
 * @param {import('./Cache.js').Cache<string, string>} tagsCache
 * @returns {string}
 */
var storeLazyElementInCache = function storeLazyElementInCache(tagName, registry, tagsCache) {
  var tag = createUniqueTag(tagName, registry);
  if (!tagsCache) {
    throw new Error('Lazy scoped elements requires the use of tags cache');
  }
  tagsCache.set(tagName, tag);
  return tag;
};

/**
 * Define a scoped custom element storing the scoped tag name in the cache
 *
 * @param {string} tagName
 * @param {typeof HTMLElement} klass
 * @param {import('./Cache.js').Cache<string, string>} tagsCache
 * @returns {string}
 */
var defineElementAndStoreInCache = function defineElementAndStoreInCache(tagName, klass, tagsCache) {
  var registry = customElements;
  if (!extendsHTMLElement(klass)) {
    return storeLazyElementInCache(tagName, registry, tagsCache);
  }
  if (klass === customElements.get(tagName)) {
    addToGlobalTagsCache(tagName, klass);
    return tagName;
  }
  var tag = createUniqueTag(tagName, registry);
  // @ts-ignore
  // we extend it just in case the class has been defined manually
  defineElement(tag, klass, registry);
  return tag;
};

/**
 * Gets a scoped tag name from the cache or generates a new one and defines the element if needed
 *
 * @exports
 * @param {string} tagName
 * @param {typeof HTMLElement} klass
 * @param {import('./Cache.js').Cache<string, string>} tagsCache
 * @returns {string}
 */
function registerElement(tagName, klass) {
  var tagsCache = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : undefined;
  var tag = getFromGlobalTagsCache(klass) || tagsCache && tagsCache.get(tagName) || defineElementAndStoreInCache(tagName, klass, tagsCache);
  return tag;
}

/**
 * Defines a lazy element
 *
 * @param {string} tagName
 * @param {typeof HTMLElement} klass
 * @param {import('./Cache.js').Cache<string, string>} tagsCache
 */
function defineScopedElement(tagName, klass, tagsCache) {
  var tag = tagsCache.get(tagName);
  if (tag) {
    if (customElements.get(tag) === undefined) {
      defineElement(tag, klass, customElements);
    }
  } else {
    tagsCache.set(tagName, registerElement(tagName, klass, tagsCache));
  }
}

export { defineScopedElement, registerElement };
